import {
    compareSchedulesByName,
    groupScheduleTemplates,
    searchScheduleTemplates,
} from "./scheduleListHelpers";
import { SchedulePickerItem } from "./SchedulePicker";

export const NO_SCHEDULE_ITEM = {
    id: "schedule-picker:no-schedule",
    name: "No schedule",
} as const;

export type NoScheduleItem = typeof NO_SCHEDULE_ITEM;

/**
 * Amount / term changes on the schedule already applied. Distinct from picking
 * a different template, which replaces the schedule rather than re-pricing it.
 */
export const CHANGE_SCHEDULE_ITEM = {
    id: "schedule-picker:change-schedule",
    name: "Edit this schedule",
} as const;

export type ChangeScheduleItem = typeof CHANGE_SCHEDULE_ITEM;

/** Restarts a paused schedule; offered only while the applied one is paused. */
export const RESUME_SCHEDULE_ITEM = {
    id: "schedule-picker:resume-schedule",
    name: "Resume schedule…",
} as const;

export type ResumeScheduleItem = typeof RESUME_SCHEDULE_ITEM;

export type SchedulePickerValue<TTemplate extends SchedulePickerItem> =
    | TTemplate
    | NoScheduleItem
    | ChangeScheduleItem
    | ResumeScheduleItem;

export function isNoScheduleItem<TTemplate extends SchedulePickerItem>(
    value: SchedulePickerValue<TTemplate>,
): value is NoScheduleItem {
    return value.id === NO_SCHEDULE_ITEM.id;
}

export function isChangeScheduleItem<TTemplate extends SchedulePickerItem>(
    value: SchedulePickerValue<TTemplate>,
): value is ChangeScheduleItem {
    return value.id === CHANGE_SCHEDULE_ITEM.id;
}

export function isResumeScheduleItem<TTemplate extends SchedulePickerItem>(
    value: SchedulePickerValue<TTemplate>,
): value is ResumeScheduleItem {
    return value.id === RESUME_SCHEDULE_ITEM.id;
}

export interface SchedulePickerGroup<TTemplate extends SchedulePickerItem> {
    name: string;
    templates: TTemplate[];
    isExpanded: boolean;
}

export interface SchedulePickerState<TTemplate extends SchedulePickerItem> {
    groups: Array<SchedulePickerGroup<TTemplate>>;
    hasNoScheduleRow: boolean;
    hasChangeScheduleRow: boolean;
    hasResumeScheduleRow: boolean;
    /** Everything currently selectable, in render order, for keyboard navigation. */
    items: Array<SchedulePickerValue<TTemplate>>;
}

export function toggleCollapsedGroup(
    collapsedGroupNames: ReadonlySet<string>,
    groupName: string,
): ReadonlySet<string> {
    const next = new Set(collapsedGroupNames);
    if (next.has(groupName)) {
        next.delete(groupName);
    } else {
        next.add(groupName);
    }
    return next;
}

/**
 * What the picker shows for a search term and set of collapsed groups. A
 * search expands every group (as the category filter tree does) and hides the
 * pinned "No schedule" row, which is an action rather than a match.
 */
export function resolveSchedulePickerState<
    TTemplate extends SchedulePickerItem,
>(options: {
    templates: TTemplate[];
    search: string;
    prioritizedTemplateId?: string;
    collapsedGroupNames: ReadonlySet<string>;
    canClear: boolean;
    canChange?: boolean;
    canResume?: boolean;
}): SchedulePickerState<TTemplate> {
    const isSearching = options.search.trim() !== "";
    const groups = groupScheduleTemplates(
        searchScheduleTemplates(options.templates, options.search),
        (leftTemplate, rightTemplate) => {
            if (leftTemplate.id === options.prioritizedTemplateId) {
                return -1;
            }
            if (rightTemplate.id === options.prioritizedTemplateId) {
                return 1;
            }
            return compareSchedulesByName(leftTemplate, rightTemplate);
        },
    ).map((group) => ({
        ...group,
        isExpanded: isSearching || !options.collapsedGroupNames.has(group.name),
    }));
    const hasNoScheduleRow = options.canClear && !isSearching;
    const hasChangeScheduleRow = options.canChange === true && !isSearching;
    const hasResumeScheduleRow = options.canResume === true && !isSearching;
    return {
        groups,
        hasNoScheduleRow,
        hasChangeScheduleRow,
        hasResumeScheduleRow,
        items: [
            ...(hasResumeScheduleRow ? [RESUME_SCHEDULE_ITEM] : []),
            ...(hasChangeScheduleRow ? [CHANGE_SCHEDULE_ITEM] : []),
            ...(hasNoScheduleRow ? [NO_SCHEDULE_ITEM] : []),
            ...groups.flatMap((group) =>
                group.isExpanded ? group.templates : [],
            ),
        ],
    };
}
